import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Download,
  RefreshCw,
  Trash2,
  ChevronRight,
  ChevronDown,
  Bot,
  Sparkles,
  FileCheck,
  Import,
  X,
  Building2,
  DollarSign,
  Calendar,
  User,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ============== Types ==============
// Backend response fields mapped to frontend interface
// Includes both backend field names and legacy frontend field names for compatibility
type DocumentStatus = 'PENDING' | 'CLASSIFYING' | 'QUEUED' | 'PROCESSING' | 'EXTRACTING' | 'EXTRACTED' | 'REVIEW' | 'NEEDS_REVIEW' | 'APPROVED' | 'REJECTED' | 'IMPORTING' | 'IMPORTED' | 'FAILED' | 'UPLOADED';
type DocumentCategory = 'TAX' | 'K1' | 'CAPITAL_ACTIVITY' | 'CAPITAL_CALL' | 'DISTRIBUTION' | 'COMPLIANCE' | 'CREDIT' | 'STATEMENTS' | 'CUSTODIAN_STATEMENT' | 'PERFORMANCE_REPORT' | 'UNKNOWN' | 'OTHER';

interface DocumentInbox {
  id: string;
  // Backend uses originalFilename, filename
  fileName: string;
  originalFilename?: string;
  filename?: string;
  fileType: string;
  mimeType?: string;
  fileSize: number;
  status: DocumentStatus;
  // Backend uses documentCategory
  category?: DocumentCategory;
  documentCategory?: DocumentCategory;
  // Backend uses classificationConfidence
  confidenceScore?: number;
  classificationConfidence?: number;
  extractedData?: any;
  uploadedBy: number;
  // Backend uses createdAt
  uploadedAt?: string;
  createdAt?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  importedAt?: string;
  errorMessage?: string;
}

interface DocumentStats {
  total: number;
  pending: number;
  needsReview: number;
  approved: number;
  imported: number;
  failed: number;
}

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 24px;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const UploadZone = styled.div<{ isDragging: boolean }>`
  border: 2px dashed ${props => props.isDragging ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  padding: 48px;
  text-align: center;
  transition: all 0.3s ease;
  background: ${props => props.isDragging ? 'rgba(99, 102, 241, 0.1)' : 'transparent'};
  cursor: pointer;

  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
    background: rgba(99, 102, 241, 0.05);
  }
`;

// ============== Helper Functions ==============
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateValue: string | number[] | null | undefined) => {
  if (!dateValue) return 'N/A';

  let date: Date;

  // Handle array format from Jackson LocalDateTime serialization [year, month, day, hour, minute, second, nano]
  if (Array.isArray(dateValue)) {
    const [year, month, day, hour = 0, minute = 0] = dateValue;
    date = new Date(year, month - 1, day, hour, minute);
  } else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  } else {
    return 'N/A';
  }

  if (isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getCategoryIcon = (category?: string) => {
  switch (category) {
    case 'K1':
    case 'TAX':
      return <FileSpreadsheet className="w-5 h-5 text-amber-400" />;
    case 'CAPITAL_CALL':
    case 'CAPITAL_ACTIVITY':
      return <DollarSign className="w-5 h-5 text-rose-400" />;
    case 'DISTRIBUTION':
      return <DollarSign className="w-5 h-5 text-emerald-400" />;
    case 'CUSTODIAN_STATEMENT':
    case 'STATEMENTS':
      return <Building2 className="w-5 h-5 text-blue-400" />;
    case 'PERFORMANCE_REPORT':
      return <FileCheck className="w-5 h-5 text-violet-400" />;
    case 'COMPLIANCE':
      return <FileCheck className="w-5 h-5 text-indigo-400" />;
    case 'CREDIT':
      return <DollarSign className="w-5 h-5 text-cyan-400" />;
    default:
      return <FileText className="w-5 h-5 text-slate-400" />;
  }
};

const getStatusBadge = (status: string) => {
  const configs: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    'PENDING': { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: <Clock className="w-3 h-3" /> },
    'UPLOADED': { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: <Clock className="w-3 h-3" /> },
    'CLASSIFYING': { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    'QUEUED': { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: <Clock className="w-3 h-3" /> },
    'PROCESSING': { bg: 'bg-indigo-500/20', text: 'text-indigo-400', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    'EXTRACTING': { bg: 'bg-indigo-500/20', text: 'text-indigo-400', icon: <Bot className="w-3 h-3 animate-pulse" /> },
    'EXTRACTED': { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <Sparkles className="w-3 h-3" /> },
    'REVIEW': { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <AlertTriangle className="w-3 h-3" /> },
    'NEEDS_REVIEW': { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <AlertTriangle className="w-3 h-3" /> },
    'APPROVED': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <CheckCircle className="w-3 h-3" /> },
    'IMPORTING': { bg: 'bg-violet-500/20', text: 'text-violet-400', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    'IMPORTED': { bg: 'bg-violet-500/20', text: 'text-violet-400', icon: <Import className="w-3 h-3" /> },
    'REJECTED': { bg: 'bg-rose-500/20', text: 'text-rose-400', icon: <XCircle className="w-3 h-3" /> },
    'FAILED': { bg: 'bg-red-500/20', text: 'text-red-400', icon: <XCircle className="w-3 h-3" /> }
  };

  const config = configs[status] || configs['PENDING'];

  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium", config.bg, config.text)}>
      {config.icon}
      {status.replace('_', ' ')}
    </span>
  );
};

// Format extracted data values (handles date arrays, nulls, currency, etc.)
const formatExtractedValue = (key: string, value: any): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  // Handle date arrays from Jackson LocalDate serialization [year, month, day]
  if (Array.isArray(value) && value.length >= 3 && value.length <= 7) {
    const [year, month, day] = value;
    // Check if it looks like a date (year > 1900, month 1-12, day 1-31)
    if (typeof year === 'number' && year > 1900 && year < 2100 &&
        typeof month === 'number' && month >= 1 && month <= 12 &&
        typeof day === 'number' && day >= 1 && day <= 31) {
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }

  // Handle currency-related fields
  const currencyFields = ['amount', 'totalAmount', 'investorAmount', 'callAmount', 'distributionAmount'];
  if (currencyFields.some(f => key.toLowerCase().includes(f.toLowerCase())) && typeof value === 'number') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }

  // Handle percentages
  if (key.toLowerCase().includes('percentage') && typeof value === 'number') {
    return `${(value * 100).toFixed(2)}%`;
  }

  return String(value);
};

// Get a user-friendly label for extracted data keys
const formatExtractedKey = (key: string): string => {
  const keyLabels: Record<string, string> = {
    'fundName': 'Fund Name',
    'dueDate': 'Due Date',
    'noticeDate': 'Notice Date',
    'paymentDate': 'Payment Date',
    'recordDate': 'Record Date',
    'distributionDate': 'Distribution Date',
    'activityType': 'Activity Type',
    'totalAmount': 'Total Amount',
    'investorAmount': 'Investor Amount',
    'commitmentAmount': 'Commitment',
    'unfundedCommitment': 'Unfunded',
    'returnOfCapital': 'Return of Capital',
    'realizedGain': 'Realized Gain',
    'carriedInterest': 'Carried Interest',
    'managementFee': 'Management Fee',
    'parserType': 'Parser Type',
    'status': 'Status',
    'fundVintage': 'Fund Vintage',
    'fundType': 'Fund Type',
    'taxYear': 'Tax Year',
    'partnerName': 'Partner Name',
    'extractionConfidence': 'Confidence',
    'extractionId': 'Extraction ID'
  };
  return keyLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
};

const getCategoryLabel = (category?: string) => {
  const labels: Record<string, string> = {
    'K1': 'K-1 Tax Document',
    'TAX': 'Tax Document',
    'CAPITAL_CALL': 'Capital Call Notice',
    'CAPITAL_ACTIVITY': 'Capital Activity',
    'DISTRIBUTION': 'Distribution Notice',
    'CUSTODIAN_STATEMENT': 'Custodian Statement',
    'STATEMENTS': 'Statement',
    'PERFORMANCE_REPORT': 'Performance Report',
    'COMPLIANCE': 'Compliance Document',
    'CREDIT': 'Credit Document',
    'UNKNOWN': 'Other Document',
    'OTHER': 'Other Document'
  };
  return labels[category || 'UNKNOWN'] || 'Other Document';
};

// ============== Main Component ==============
const DocumentInboxScreen: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentInbox[]>([]);
  const [stats, setStats] = useState<DocumentStats>({ total: 0, pending: 0, needsReview: 0, approved: 0, imported: 0, failed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentInbox | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [screenError, setScreenError] = useState<string | null>(null);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    setScreenError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCategory !== 'all') params.append('category', filterCategory);

      const response = await axios.get(`/api/document-inbox?${params.toString()}`);
      setDocuments(response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
      setScreenError('Unable to load document inbox from OMS APIs.');
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterCategory]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/document-inbox/stats');
      setStats(response.data);
    } catch (error) {
      setStats({ total: 0, pending: 0, needsReview: 0, approved: 0, imported: 0, failed: 0 });
      setScreenError((prev) => prev || 'Unable to load document stats.');
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments, fetchStats]);

  // File upload handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await uploadFiles(files);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      await uploadFiles(files);
    }
  };

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      await axios.post('/api/document-inbox/upload/batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      fetchDocuments();
      fetchStats();
    } catch (error) {
      console.error('Upload failed:', error);
      setScreenError('Upload failed. No local mock documents were created.');
    } finally {
      setIsUploading(false);
    }
  };

  // Document actions
  const handleApprove = async (docId: string) => {
    try {
      const response = await axios.post(`/api/document-inbox/${docId}/approve`);
      console.log('Approve response:', response.data);
      // Update selectedDoc with new status
      if (selectedDoc?.id === docId) {
        setSelectedDoc({ ...selectedDoc, status: 'APPROVED' });
      }
      fetchDocuments();
      fetchStats();
    } catch (error: any) {
      console.error('Approve error:', error?.response?.data || error.message);
      setScreenError('Approve failed. Document status not changed locally.');
    }
  };

  const handleReject = async (docId: string, reason: string) => {
    try {
      await axios.post(`/api/document-inbox/${docId}/reject`, { reason });
      if (selectedDoc?.id === docId) {
        setSelectedDoc({ ...selectedDoc, status: 'REJECTED' });
      }
      fetchDocuments();
      fetchStats();
    } catch (error) {
      setScreenError('Reject failed. Document status not changed locally.');
    }
  };

  const handleImport = async (docId: string) => {
    try {
      await axios.post(`/api/document-inbox/${docId}/import`);
      if (selectedDoc?.id === docId) {
        setSelectedDoc({ ...selectedDoc, status: 'IMPORTED' });
      }
      fetchDocuments();
      fetchStats();
    } catch (error) {
      setScreenError('Import failed. Document status not changed locally.');
    }
  };

  const handleRetry = async (docId: string) => {
    try {
      await axios.post(`/api/document-inbox/${docId}/retry`);
      if (selectedDoc?.id === docId) {
        setSelectedDoc({ ...selectedDoc, status: 'CLASSIFYING' });
      }
      fetchDocuments();
    } catch (error) {
      setScreenError('Retry failed. Document status not changed locally.');
    }
  };

  const handleViewOriginal = async (doc: DocumentInbox) => {
    // Get the file path from the document and open/download it
    const filePath = (doc as any).filePath;
    if (filePath) {
      // Try to open the document
      try {
        const response = await axios.get(`/api/document-inbox/${doc.id}/download`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: doc.mimeType || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (error) {
        console.error('Failed to download document:', error);
        alert('Unable to view document. File may not be accessible.');
      }
    } else {
      alert('Document file path not available');
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    if (searchTerm && !doc.fileName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <Container>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Document Inbox</h1>
            <p className="text-slate-400 text-sm">AI-powered document processing for K-1s, capital calls, and statements</p>
          </div>
        </div>
        <button
          onClick={() => { fetchDocuments(); fetchStats(); }}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-sm font-medium text-slate-300 hover:text-white transition-all flex items-center gap-2"
        >
          <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          Refresh
        </button>
      </div>
      {screenError && (
        <div className="mb-4 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm text-rose-300">
          {screenError}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'text-white' },
          { label: 'Processing', value: stats.pending, color: 'text-blue-400' },
          { label: 'Needs Review', value: stats.needsReview, color: 'text-amber-400' },
          { label: 'Approved', value: stats.approved, color: 'text-emerald-400' },
          { label: 'Imported', value: stats.imported, color: 'text-violet-400' },
          { label: 'Failed', value: stats.failed, color: 'text-rose-400' }
        ].map(stat => (
          <GlassCard key={stat.label} className="p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{stat.label}</div>
            <div className={cn("text-2xl font-bold", stat.color)}>{stat.value}</div>
          </GlassCard>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Document List */}
        <div className="col-span-2 space-y-6">
          {/* Upload Zone */}
          <GlassCard>
            <UploadZone
              isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.tiff"
                onChange={handleFileSelect}
                className="hidden"
              />
              {isUploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-12 h-12 text-indigo-400 animate-spin" />
                  <p className="text-slate-300 font-medium">Processing documents...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-300 font-medium mb-2">Drop documents here or click to upload</p>
                  <p className="text-xs text-slate-500">Supports PDF, PNG, JPG, TIFF</p>
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <span className="text-[10px] text-slate-600 uppercase tracking-wider">Auto-classifies:</span>
                    {['K-1', 'Capital Calls', 'Distributions', 'Statements'].map(type => (
                      <span key={type} className="px-2 py-1 bg-slate-800 rounded text-[10px] text-slate-400">{type}</span>
                    ))}
                  </div>
                </>
              )}
            </UploadZone>
          </GlassCard>

          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search documents..."
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-white/5 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-white/5 rounded-lg text-sm text-white focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="EXTRACTED">Needs Review</option>
              <option value="APPROVED">Approved</option>
              <option value="IMPORTED">Imported</option>
              <option value="FAILED">Failed</option>
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-white/5 rounded-lg text-sm text-white focus:outline-none"
            >
              <option value="all">All Types</option>
              <option value="TAX">Tax (K-1)</option>
              <option value="CAPITAL_ACTIVITY">Capital Activity</option>
              <option value="COMPLIANCE">Compliance</option>
              <option value="STATEMENTS">Statements</option>
              <option value="CREDIT">Credit</option>
            </select>
          </div>

          {/* Document List */}
          <GlassCard>
            <div className="divide-y divide-white/5">
              {filteredDocuments.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No documents found</p>
                  <p className="text-xs text-slate-600 mt-1">Upload documents to get started</p>
                </div>
              ) : (
                filteredDocuments.map((doc) => {
                  // Map backend field names to frontend expectations
                  const displayName = doc.originalFilename || doc.filename || doc.fileName || 'Unknown';
                  const displayCategory = doc.documentCategory || doc.category;
                  const displayConfidence = doc.classificationConfidence || doc.confidenceScore;
                  const displayDate = doc.createdAt || doc.uploadedAt;

                  return (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={cn(
                        "p-4 hover:bg-white/5 cursor-pointer transition-colors",
                        selectedDoc?.id === doc.id && "bg-indigo-500/10"
                      )}
                      onClick={() => setSelectedDoc(doc)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                          {getCategoryIcon(displayCategory)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-white truncate">{displayName}</span>
                            {displayConfidence && (
                              <span className="text-[10px] text-slate-500">
                                {Math.round(Number(displayConfidence) * 100)}% confidence
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span>{getCategoryLabel(displayCategory)}</span>
                            <span>•</span>
                            <span>{formatFileSize(doc.fileSize)}</span>
                            <span>•</span>
                            <span>{formatDate(displayDate)}</span>
                          </div>
                        </div>
                        {getStatusBadge(doc.status)}
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </GlassCard>
        </div>

        {/* Right: Document Detail */}
        <div className="col-span-1">
          <GlassCard className="sticky top-4">
            {selectedDoc ? (
              (() => {
                // Map backend field names for selected document
                const displayName = selectedDoc.originalFilename || selectedDoc.filename || selectedDoc.fileName || 'Unknown';
                const displayCategory = selectedDoc.documentCategory || selectedDoc.category;
                const displayConfidence = selectedDoc.classificationConfidence || selectedDoc.confidenceScore;
                const displayDate = selectedDoc.createdAt || selectedDoc.uploadedAt;

                return (
              <div>
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white">Document Details</h3>
                  <button onClick={() => setSelectedDoc(null)} className="p-1 hover:bg-white/5 rounded">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  {/* File Info */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
                      {getCategoryIcon(displayCategory)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white truncate max-w-[200px]">{displayName}</div>
                      <div className="text-xs text-slate-500">{formatFileSize(selectedDoc.fileSize)}</div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-2">Status</div>
                    {getStatusBadge(selectedDoc.status)}
                  </div>

                  {/* Classification */}
                  {displayCategory && (
                    <div className="p-3 bg-slate-900/50 rounded-lg">
                      <div className="text-xs text-slate-500 mb-2">Classification</div>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(displayCategory)}
                        <span className="text-sm text-white">{getCategoryLabel(displayCategory)}</span>
                      </div>
                      {displayConfidence && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${Number(displayConfidence) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-400">{Math.round(Number(displayConfidence) * 100)}%</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Extracted Data Preview */}
                  {selectedDoc.extractedData && (
                    <div className="p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs text-slate-500">AI Extracted Data</span>
                      </div>
                      <div className="space-y-2 text-xs">
                        {(() => {
                          const data = selectedDoc.extractedData;
                          // Prioritize important fields based on document type
                          const priorityFields = ['status', 'activityType', 'fundName', 'investorAmount', 'totalAmount',
                            'noticeDate', 'paymentDate', 'dueDate', 'fundVintage', 'parserType', 'extractionConfidence'];
                          const sortedEntries = Object.entries(data)
                            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                            .sort(([a], [b]) => {
                              const aIdx = priorityFields.indexOf(a);
                              const bIdx = priorityFields.indexOf(b);
                              if (aIdx === -1 && bIdx === -1) return 0;
                              if (aIdx === -1) return 1;
                              if (bIdx === -1) return -1;
                              return aIdx - bIdx;
                            });
                          return sortedEntries.slice(0, 8).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-slate-500">{formatExtractedKey(key)}</span>
                              <span className="text-slate-300 font-medium">{formatExtractedValue(key, value)}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-3">Timeline</div>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <Upload className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">Uploaded {formatDate(displayDate)}</span>
                      </div>
                      {selectedDoc.reviewedAt && (
                        <div className="flex items-center gap-2">
                          <Eye className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-400">Reviewed {formatDate(selectedDoc.reviewedAt)}</span>
                        </div>
                      )}
                      {selectedDoc.importedAt && (
                        <div className="flex items-center gap-2">
                          <Import className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-400">Imported {formatDate(selectedDoc.importedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Error Message */}
                  {selectedDoc.errorMessage && (
                    <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg">
                      <div className="flex items-center gap-2 text-rose-400 text-xs">
                        <AlertTriangle className="w-4 h-4" />
                        {selectedDoc.errorMessage}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 border-t border-white/5 space-y-2">
                    {(selectedDoc.status === 'NEEDS_REVIEW' || selectedDoc.status === 'EXTRACTED' || selectedDoc.status === 'REVIEW') && (
                      <>
                        <button
                          onClick={() => handleApprove(selectedDoc.id)}
                          className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold text-white transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve Extraction
                        </button>
                        <button
                          onClick={() => handleReject(selectedDoc.id, 'Incorrect extraction')}
                          className="w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-sm font-medium text-slate-300 transition-colors flex items-center justify-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}

                    {selectedDoc.status === 'APPROVED' && (
                      <button
                        onClick={() => handleImport(selectedDoc.id)}
                        className="w-full px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-sm font-bold text-white transition-colors flex items-center justify-center gap-2"
                      >
                        <Import className="w-4 h-4" />
                        Import to OMS
                      </button>
                    )}

                    {selectedDoc.status === 'FAILED' && (
                      <button
                        onClick={() => handleRetry(selectedDoc.id)}
                        className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm font-bold text-white transition-colors flex items-center justify-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Retry Processing
                      </button>
                    )}

                    <button
                      onClick={() => handleViewOriginal(selectedDoc)}
                      className="w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-sm font-medium text-slate-300 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Original
                    </button>
                  </div>
                </div>
              </div>
                );
              })()
            ) : (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-sm">Select a document to view details</p>
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </Container>
  );
};

export default DocumentInboxScreen;
